import {
    Box,
    Button,
    Collapse,
    Icon,
    IconButton,
    LinearProgress,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import {
    CountDocuments,
    DocumentFilters,
    DocumentPicker,
    FixedLoadingIndicator,
    LabelDisplayedRows,
    RefundsCell,
    Search,
    ServerErrorHandler,
} from "components"
import { AuthContext, DocumentContext, ManagementContext } from "context"
import {
    DASHBOARD_TYPES,
    DOCUMENT_PATH,
    DOCUMENT_STATES,
    DOCUMENT_TITLE,
    DOCUMENT_TYPE_CODES,
    DOCUMENT_TYPE_IDS,
    DOCUMENT_VIEW_PATH,
    DOCUMENTS_TABLE_COLUMNS,
    FIELDS,
    FINISHED_PRINTED_INVOICES_COLUMNS,
    getDocumentByIdService,
    getPurchaseOrderByIdService,
    MEDIUM_ROWS_PER_PAGINATION_OPTIONS,
    PURCHASE_ORDER_PATH,
    PURCHASE_ORDER_VIEW_PATH,
    ROOT_TITLE,
    SINGLE_COMPANY_DOCUMENTS_TABLE_COLUMNS
} from "lib"
import moment from 'moment'
import React from "react"
import { Helmet } from "react-helmet"
import { useHistory } from "react-router"
import { IDocument, IMessageConfig } from "types"

interface Props {
    initialCode?: string
    initialState?: number
    initialHistoryType?: number
    typePath?: string
    redirectOnApprove?: string
    redirectOnReject?: string
    hideState?: boolean
    hideType?: boolean
    forEmission?: boolean
    forReception?: boolean
    showCounts?: boolean
    issuingCompany?: boolean
    receivingCompany?: boolean
    countPath?: string
    createRetentionOnly?: boolean
}

const DocumentsDashboard = (props: Props) => {
    const {
        initialCode,
        typePath,
        issuingCompany,
        initialState,
        redirectOnApprove,
        hideState,
        hideType,
        forEmission,
        forReception,
        showCounts,
        redirectOnReject,
        initialHistoryType,
        receivingCompany,
        countPath,
        createRetentionOnly
    } = props
    const {
        documents,
        company,
        conditions,
        documentFilters: filters,
        setFilters,
        documentsModel,
        documentTypes,
        getDocumentsByFilters,
        totalDocuments,
        getNextDocumentsByFilters,
        getDocumentsByInitialFilters,
        historyTypes,
        setRetentionReceiptView
    } = React.useContext(DocumentContext)
    const { user } = React.useContext(AuthContext)
    const { createSortHandler, handleChangePage, handleChangeRowsPerPage, order, orderBy, page, rowsPerPage, } = documentsModel
    const history = useHistory()
    const { refreshIdentificationTypes, refreshTaxes, refreshTaxTypes, identificationTypes, taxTypes, taxes } = React.useContext(ManagementContext)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false)
    const [error, setError] = React.useState<boolean>(false)
    const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({ open: false, message: "", severity: "info", })
    const [openPicker, setOpenPicker] = React.useState<boolean>(false)
    const [openFilters, setOpenFilters] = React.useState<boolean>(false)
    const handleFilter = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            setLoading(true)
            await getDocumentsByFilters(DASHBOARD_TYPES.documents)
            handleChangePage(undefined, 0)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            setError(true)
        }
    }
    const handleNewPage = async (pageNumber: number, offset: number) => {
        try {
            setLoading(true)
            await getNextDocumentsByFilters(offset, pageNumber, DASHBOARD_TYPES.documents)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            setError(true)
        }
    }
    const handleSelect = async (document: IDocument) => {
        try {
            setLoading(true)
            if (identificationTypes.length === 0) { refreshIdentificationTypes() }
            if (taxTypes.length === 0) { refreshTaxTypes() }
            if (taxes.length === 0) { refreshTaxes() }
            const response = await getDocumentByIdService(document.documentId)
            history.push({
                state: { document: response, redirectOnApprove, redirectOnReject },
                pathname: `${DOCUMENT_PATH}${DOCUMENT_VIEW_PATH}/${document.documentId}`
            })
            setLoading(false)
        } catch (error) {
            console.log(error)
            setMessageConfig({ open: true, message: "No se pudo obtener información del documento selecionado...", severity: "error" })
            setLoading(false)
        }
    }
    const handleClearFilters = async () => {
        try {
            setLoading(true)
            await getDocumentsByInitialFilters(initialCode, initialState, issuingCompany ? company?.identification : undefined, initialHistoryType, undefined, DASHBOARD_TYPES.documents)
            handleChangePage(undefined, 0)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            setError(true)
        }
    }
    const documentsTable = React.useMemo((): IDocument[] => {
        return (initialLoading ? [] : documents)
    }, [documents, initialLoading])


    const isFinishedInvoice = React.useCallback(() => {
        return initialState === DOCUMENT_STATES.finished && (initialCode === DOCUMENT_TYPE_CODES.printedInvoice || initialCode === DOCUMENT_TYPE_CODES.electronicInvoice)
    }, [initialState, initialCode])
    const isRetentionReceipt = React.useCallback(() => {
        return initialCode === DOCUMENT_TYPE_CODES.retentionReceipt
    }, [initialCode])

    // Informa al contexto que esta vista (document/retention-receipt) trata el filtro
    // "Estado proceso" vacio como "los 3 estados de retencion" (100/101/102).
    React.useEffect(() => {
        setRetentionReceiptView(initialCode === DOCUMENT_TYPE_CODES.retentionReceipt)
        return () => setRetentionReceiptView(false)
    }, [initialCode, setRetentionReceiptView])
    const isFinishedForeignExpensesLiquidation = React.useCallback(() => {
        return initialCode === DOCUMENT_TYPE_CODES.foreignExpensesLiquidation && initialState === DOCUMENT_STATES.finished
    }, [initialCode, initialState])
    const isReimbursementInvoice = React.useCallback(() => {
        return initialCode === DOCUMENT_TYPE_CODES.reimbursementInvoice
    }, [initialCode])
    const isForeignExpensesLiquidationNotFinished = React.useCallback(() => {
        return initialCode === DOCUMENT_TYPE_CODES.foreignExpensesLiquidation && initialState !== DOCUMENT_STATES.finished
    }, [initialCode, initialState])
    const isPurchaseSettlement = React.useCallback(() => {
        return initialCode === DOCUMENT_TYPE_CODES.purchaseSettlement
    }, [initialCode])
    const applyingFilters = React.useCallback((): boolean => {
        return Boolean((
            filters.conditions.length > 0
            || filters.documentTypes.length > 0
            || Boolean(filters.rucOrName) || Boolean(filters.serialNumber)
            || filters.documentRange.start !== null
            || filters.documentRange.end !== null
            || filters.receptionRange.start !== null
            || filters.receptionRange.end !== null
            || filters.rucOrNameReceptor !== null
        ))
    }, [filters])

    const handlePurchaseOrder = async (id: number) => {
        try {
            setLoading(true)
            const purchaseOrder = await getPurchaseOrderByIdService(id)
            history.push({
                state: { purchaseOrder },
                pathname: `${PURCHASE_ORDER_PATH}${PURCHASE_ORDER_VIEW_PATH}/${purchaseOrder.document.documentId}`
            })
            setLoading(false)
        } catch (error) {
            setLoading(false)
            setMessageConfig({ open: true, message: "No se pudo obtener información del documento selecionado...", severity: "error" })
        }
    }


    const obtenerNombreDocumento = (row: IDocument) => {
        const name = documentTypes.find(d => d.documentTypeId === row.documentTypeId)?.documentName
        if (name === 'COMPROBANTE DE RETENCIÓN') {
            if (row.sriReportId === 2) {
                return 'RETENCIÓN RENTA'
            } else {
                return 'RETENCIÓN IVA'
            }
        } else {
            return name
        }
    }

    React.useEffect(() => {
        const initScreen = async () => {
            try {
                if (history.action === "REPLACE" || history.action === "PUSH") {
                    setInitialLoading(true)
                    await getDocumentsByInitialFilters(initialCode, initialState, issuingCompany ? company?.identification : undefined, initialHistoryType, receivingCompany ? company?.identification : undefined, DASHBOARD_TYPES.documents)
                    handleChangePage(undefined, 0)
                    setInitialLoading(false)
                }
            } catch (error) {
                setInitialLoading(false)
                setError(true)
            }
        }
        initScreen()
    }, [initialCode, getDocumentsByInitialFilters, history, initialState, handleChangePage, issuingCompany, company, initialHistoryType, receivingCompany])


    return (
        <Paper className="flex flex-col h-full overflow-hidden p-4 pb-0">
            <Helmet>
                <title>{`${ROOT_TITLE} - ${DOCUMENT_TITLE}`}</title>
            </Helmet>
            <div className="flex items-center">
                <form className="flex flex-col w-full" onSubmit={handleFilter}>
                    {
                        showCounts &&
                        <CountDocuments countPath={DOCUMENT_PATH} />
                    }
                    <div className="flex items-center justify-between w-full">
                        <div className="flex">
                            <Search
                                onChange={(e) => setFilters({ ...filters, serialNumber: e.target.value })}
                                query={filters.serialNumber ?? ""}
                                onClear={() => setFilters({ ...filters, serialNumber: "" })}
                                placeholer="Busca por num de serie o asiento"
                                width={280}
                            />
                            {
                                !issuingCompany ?
                                    <div className="ml-2">
                                        <Search
                                            onChange={(e) => setFilters({ ...filters, rucOrName: e.target.value })}
                                            query={filters.rucOrName}
                                            onClear={() => setFilters({ ...filters, rucOrName: "" })}
                                            placeholer="RIF o nombre..."
                                            width={250}
                                        />
                                    </div>
                                    :
                                    <div className="ml-2">
                                        <Search
                                            onChange={(e) => setFilters({ ...filters, rucOrNameReceptor: e.target.value })}
                                            query={filters.rucOrNameReceptor}
                                            onClear={() => setFilters({ ...filters, rucOrNameReceptor: "" })}
                                            placeholer="RIF o nombre de receptor..."
                                            width={250}
                                        />
                                    </div>
                            }
                            <Button
                                size="small"
                                style={{ marginLeft: 20 }}
                                disableElevation
                                color={applyingFilters() ? "primary" : undefined}
                                onClick={() => setOpenFilters(current => !current)}
                            >
                                <Icon fontSize="small" style={{ marginRight: 5 }}>{openFilters ? "expand_less" : "expand_more"}</Icon>
                                <span className="pr-2">{`${openFilters ? "Menos filtros" : "Más filtros"}`}</span>
                            </Button>
                            {
                                applyingFilters() &&
                                <Tooltip
                                    arrow
                                    title="Resetear filtros por defecto"
                                >
                                    <IconButton
                                        size="small"
                                        onClick={handleClearFilters}
                                        disabled={loading || initialLoading}
                                    >
                                        <Icon fontSize="small" className="mx-2">
                                            restart_alt
                                        </Icon>
                                    </IconButton>
                                </Tooltip>
                            }
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                style={{ marginLeft: 20 }}
                                disableElevation
                                type="submit"
                                disabled={loading || initialLoading}
                            >
                                {"Aplicar"}
                            </Button>
                        </div>
                        {/* <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            style={{ marginLeft: 20 }}
                            disableElevation
                            onClick={handleNewDocument}
                        >
                            <Icon fontSize="small" style={{ marginRight: 5 }}>add</Icon>
                            {"Nuevo documento"}
                        </Button> */}
                    </div>
                    <Collapse in={openFilters}>
                        <DocumentFilters
                            hideState={hideState}
                            hideType={hideType}
                            forEmission={forEmission}
                            forReception={forReception}
                            retentionReceipt={isRetentionReceipt()}
                        />
                    </Collapse>
                </form>
            </div>
            <div className="flex h-full flex-col mt-4 overflow-hidden">
                {
                    initialLoading &&
                    <LinearProgress />
                }
                <div className="flex flex-grow overflow-auto">
                    <TableContainer>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {(issuingCompany ? SINGLE_COMPANY_DOCUMENTS_TABLE_COLUMNS : DOCUMENTS_TABLE_COLUMNS).map((headCell) => (
                                        (
                                            (createRetentionOnly && headCell.id !== FIELDS.processStatus.key) || !createRetentionOnly
                                        ) &&
                                        <TableCell
                                            key={headCell.id}
                                            align={"left"}
                                            padding={"default"}
                                            sortDirection={orderBy === headCell.id ? order : false}
                                        >
                                            <TableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? order : "asc"}
                                                onClick={createSortHandler(headCell.id as any)}
                                                style={{ fontSize: "0.85em", width: (headCell.id === FIELDS.companyName.key || headCell.id === FIELDS.receiverName.key) ? 320 : (headCell.id === FIELDS.flowStatus.key || headCell.id === FIELDS.processStatus.key) ? 250 : "max-content" }}
                                            >
                                                {headCell.label}
                                                {orderBy === headCell.id ? (
                                                    <span className="hidden">
                                                        {order === "desc"
                                                            ? "sorted descending"
                                                            : "sorted ascending"}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    {
                                        isFinishedInvoice() &&
                                        <React.Fragment>
                                            {
                                                FINISHED_PRINTED_INVOICES_COLUMNS.map((headCell) => (
                                                    <TableCell
                                                        key={headCell.id}
                                                        align={"left"}
                                                        padding={"default"}
                                                        sortDirection={orderBy === headCell.id ? order : false}
                                                    >
                                                        <TableSortLabel
                                                            active={orderBy === headCell.id}
                                                            direction={orderBy === headCell.id ? order : "asc"}
                                                            onClick={createSortHandler(headCell.id as any)}
                                                            style={{ fontSize: "0.85em", width: (headCell.id === FIELDS.companyName.key || headCell.id === FIELDS.receiverName.key) ? 320 : headCell.id === FIELDS.flowStatus.key ? 200 : "max-content" }}
                                                        >
                                                            {headCell.label}
                                                            {orderBy === headCell.id ? (
                                                                <span className="hidden">
                                                                    {order === "desc"
                                                                        ? "sorted descending"
                                                                        : "sorted ascending"}
                                                                </span>
                                                            ) : null}
                                                        </TableSortLabel>
                                                    </TableCell>
                                                ))
                                            }
                                        </React.Fragment>
                                    }
                                    {
                                        isRetentionReceipt() &&
                                        <TableCell
                                            key={FIELDS.invoiceNumber.name}
                                            align={"left"}
                                            padding={"default"}
                                            sortDirection={orderBy === FIELDS.invoiceNumber.name ? order : false}
                                        >
                                            <TableSortLabel
                                                active={orderBy === FIELDS.invoiceNumber.name}
                                                direction={orderBy === FIELDS.invoiceNumber.name ? order : "asc"}
                                                onClick={createSortHandler(FIELDS.invoiceNumber.name as any)}
                                                style={{ fontSize: "0.85em", width: 200 }}
                                            >
                                                {FIELDS.invoiceNumber.name}
                                                {orderBy === FIELDS.invoiceNumber.name ? (
                                                    <span className="hidden">
                                                        {order === "desc"
                                                            ? "sorted descending"
                                                            : "sorted ascending"}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    }
                                    {
                                        isFinishedForeignExpensesLiquidation() &&
                                        <React.Fragment>
                                            <TableCell
                                                key={FIELDS.purchaseSettlement.name}
                                                align={"left"}
                                                padding={"default"}
                                                sortDirection={orderBy === FIELDS.purchaseSettlement.name ? order : false}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === FIELDS.purchaseSettlement.name}
                                                    direction={orderBy === FIELDS.purchaseSettlement.name ? order : "asc"}
                                                    onClick={createSortHandler(FIELDS.purchaseSettlement.name as any)}
                                                    style={{ fontSize: "0.85em", width: 200 }}
                                                >
                                                    {FIELDS.purchaseSettlement.name}
                                                    {orderBy === FIELDS.purchaseSettlement.name ? (
                                                        <span className="hidden">
                                                            {order === "desc"
                                                                ? "sorted descending"
                                                                : "sorted ascending"}
                                                        </span>
                                                    ) : null}
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell
                                                key={FIELDS.retention.name}
                                                align={"left"}
                                                padding={"default"}
                                                sortDirection={orderBy === FIELDS.retention.name ? order : false}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === FIELDS.retention.name}
                                                    direction={orderBy === FIELDS.retention.name ? order : "asc"}
                                                    onClick={createSortHandler(FIELDS.retention.name as any)}
                                                    style={{ fontSize: "0.85em", width: 200 }}
                                                >
                                                    {FIELDS.retention.name}
                                                    {orderBy === FIELDS.retention.name ? (
                                                        <span className="hidden">
                                                            {order === "desc"
                                                                ? "sorted descending"
                                                                : "sorted ascending"}
                                                        </span>
                                                    ) : null}
                                                </TableSortLabel>
                                            </TableCell>
                                        </React.Fragment>
                                    }
                                    {
                                        isReimbursementInvoice() &&
                                        <TableCell
                                            key={FIELDS.sustentions.name}
                                            align={"left"}
                                            padding={"default"}
                                            sortDirection={orderBy === FIELDS.sustentions.name ? order : false}
                                        >
                                            <TableSortLabel
                                                active={orderBy === FIELDS.sustentions.name}
                                                direction={orderBy === FIELDS.sustentions.name ? order : "asc"}
                                                onClick={createSortHandler(FIELDS.sustentions.name as any)}
                                                style={{ fontSize: "0.85em", width: 200 }}
                                            >
                                                {FIELDS.sustentions.name}
                                                {orderBy === FIELDS.sustentions.name ? (
                                                    <span className="hidden">
                                                        {order === "desc"
                                                            ? "sorted descending"
                                                            : "sorted ascending"}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    }
                                    {
                                        isForeignExpensesLiquidationNotFinished() &&
                                        <React.Fragment>
                                            <TableCell
                                                key={FIELDS.retentionReceipt.name}
                                                align={"left"}
                                                padding={"default"}
                                                style={{ backgroundColor: 'pink' }}
                                                sortDirection={orderBy === FIELDS.retentionReceipt.name ? order : false}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === FIELDS.retentionReceipt.name}
                                                    direction={orderBy === FIELDS.retentionReceipt.name ? order : "asc"}
                                                    onClick={createSortHandler(FIELDS.retentionReceipt.name as any)}
                                                    style={{ fontSize: "0.85em", width: 200 }}
                                                >
                                                    {FIELDS.retentionReceipt.name}
                                                    {orderBy === FIELDS.retentionReceipt.name ? (
                                                        <span className="hidden">
                                                            {order === "desc"
                                                                ? "sorted descending"
                                                                : "sorted ascending"}
                                                        </span>
                                                    ) : null}
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell
                                                key={FIELDS.purchaseSettlement.name}
                                                align={"left"}
                                                padding={"default"}
                                                sortDirection={orderBy === FIELDS.purchaseSettlement.name ? order : false}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === FIELDS.purchaseSettlement.name}
                                                    direction={orderBy === FIELDS.purchaseSettlement.name ? order : "asc"}
                                                    onClick={createSortHandler(FIELDS.purchaseSettlement.name as any)}
                                                    style={{ fontSize: "0.85em", width: 200 }}
                                                >
                                                    {FIELDS.purchaseSettlement.name}
                                                    {orderBy === FIELDS.purchaseSettlement.name ? (
                                                        <span className="hidden">
                                                            {order === "desc"
                                                                ? "sorted descending"
                                                                : "sorted ascending"}
                                                        </span>
                                                    ) : null}
                                                </TableSortLabel>
                                            </TableCell>
                                        </React.Fragment>
                                    }
                                    {
                                        isPurchaseSettlement() &&
                                        <TableCell
                                            key={FIELDS.foreignExpensesLiquidation.name}
                                            align={"left"}
                                            padding={"default"}
                                            sortDirection={orderBy === FIELDS.foreignExpensesLiquidation.name ? order : false}
                                        >
                                            <TableSortLabel
                                                active={orderBy === FIELDS.foreignExpensesLiquidation.name}
                                                direction={orderBy === FIELDS.foreignExpensesLiquidation.name ? order : "asc"}
                                                onClick={createSortHandler(FIELDS.foreignExpensesLiquidation.name as any)}
                                                style={{ fontSize: "0.85em", width: 250 }}
                                            >
                                                {FIELDS.foreignExpensesLiquidation.name}
                                                {orderBy === FIELDS.foreignExpensesLiquidation.name ? (
                                                    <span className="hidden">
                                                        {order === "desc"
                                                            ? "sorted descending"
                                                            : "sorted ascending"}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    }
                                    <TableCell padding={"default"} />
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {documentsTable.map(
                                    (row, index) => {
                                        return (
                                            <TableRow
                                                hover
                                                tabIndex={-1}
                                                key={row.documentId.toString()}
                                                className="cursor-pointer"
                                                onClick={(loading || initialLoading) ? undefined : () => handleSelect(row)}
                                            >
                                                <TableCell className="truncate" component="th" scope="row" padding="none">
                                                    {obtenerNombreDocumento(row)}
                                                </TableCell>
                                                <TableCell className="truncate" align="left" padding="default">
                                                    {
                                                        row.serialNumber.length === 15 ?
                                                            `${row.serialNumber.substring(0, 3)}-${row.serialNumber.substring(3, 6)}-${row.serialNumber.substring(6, 15)}`
                                                            : row.serialNumber
                                                    }
                                                </TableCell>
                                                  <TableCell padding="default" align="left">
                                                    {row.documentAuthorization}
                                                </TableCell>
                                                <TableCell padding="default" align="left">
                                                    {/* Se añade una hora a la fecha, ya que el servidor de producción tiene una hora adelantada
                                                    y el documentDate en la base tiene como hora 00:00:00 -5, y el servidor tiene +1 */}
                                                    {moment(new Date(row.documentDate)).add(1, "hour").format('DD-MM-YYYY').toString()}
                                                </TableCell>
                                                {/* <TableCell padding="default" align="left">
                                                    {row.authorizationDate ? moment(new Date(row.authorizationDate).toISOString()).format('DD-MM-YYYY').toString() : "---"}
                                                </TableCell> */}
                                                <TableCell padding="default" align="left">
                                                    {row.inserted ? moment(new Date(row.inserted).toISOString()).format('DD-MM-YYYY').toString() : "---"}
                                                </TableCell>
                                                <TableCell padding="default" align="left">
                                                    {issuingCompany ? row.receiverRuc : row.companyRuc}
                                                </TableCell>
                                                <TableCell className="truncate" padding="default" align="left">
                                                    {issuingCompany ? row.receiverName : row.companyName}
                                                </TableCell>
                                                {!issuingCompany &&
                                                    <TableCell padding="default" align="left">
                                                        {conditions.find(c => c.conditionId === row.flowStatus)?.name}
                                                    </TableCell>
                                                }
                                                <TableCell padding="default" align="left" style={{ whiteSpace: 'nowrap' }}>
                                                    {row.error}
                                                </TableCell>

                                                {
                                                    isFinishedInvoice() &&
                                                    <React.Fragment>
                                                        <TableCell
                                                            onClick={(e) => {
                                                                const id = row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && type.documentCode === DOCUMENT_TYPE_CODES.purchaseOrder)
                                                                })?.documentId
                                                                if (id) {
                                                                    e.stopPropagation()
                                                                    handlePurchaseOrder(id)
                                                                }
                                                            }}
                                                            className="document-link" padding="default" align="left"
                                                        >
                                                            {row.associated.find(r => {
                                                                const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                return (r.documentTypeId === type?.documentTypeId && type.documentCode === DOCUMENT_TYPE_CODES.purchaseOrder)
                                                            })?.serialNumber}
                                                        </TableCell>
                                                        <TableCell
                                                            onClick={(e) => {
                                                                const document = row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && type.documentCode === DOCUMENT_TYPE_CODES.retentionReceipt)
                                                                })
                                                                if (document) {
                                                                    e.stopPropagation()
                                                                    handleSelect(document)
                                                                }
                                                            }}
                                                            className="document-link" padding="default" align="left"
                                                        >
                                                            {row.associated.find(r => {
                                                                const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                return (r.documentTypeId === type?.documentTypeId && type.documentCode === DOCUMENT_TYPE_CODES.retentionReceipt)
                                                            })?.serialNumber}
                                                        </TableCell>
                                                    </React.Fragment>
                                                }

                                                {
                                                    isRetentionReceipt() &&
                                                    <TableCell
                                                        onClick={(e) => {
                                                            const document = row.associated.find(r =>
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.electronicInvoice ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.reimbursementInvoice ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.foreignExpensesLiquidation ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.printedInvoice
                                                            )
                                                            if (document) {
                                                                e.stopPropagation()
                                                                handleSelect(document)
                                                            }
                                                        }}
                                                        className="document-link" padding="default" align="left"
                                                    >
                                                        {
                                                            row.associated.find(r =>
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.electronicInvoice ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.reimbursementInvoice ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.foreignExpensesLiquidation ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.printedInvoice
                                                            )?.serialNumber
                                                        }
                                                    </TableCell>
                                                }
                                                {
                                                    isFinishedForeignExpensesLiquidation() &&
                                                    <React.Fragment>
                                                        <TableCell
                                                            onClick={(e) => {
                                                                const document = row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && (type.documentCode === DOCUMENT_TYPE_CODES.purchaseSettlement))
                                                                })
                                                                if (document) {
                                                                    e.stopPropagation()
                                                                    handleSelect(document)
                                                                }
                                                            }}
                                                            className="document-link" padding="default" align="left"
                                                        >
                                                            {
                                                                row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && (type.documentCode === DOCUMENT_TYPE_CODES.purchaseSettlement))
                                                                })?.serialNumber
                                                            }
                                                        </TableCell>
                                                        <TableCell
                                                            onClick={(e) => {
                                                                const document = row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && (type.documentCode === DOCUMENT_TYPE_CODES.retentionReceipt))
                                                                })
                                                                if (document) {
                                                                    e.stopPropagation()
                                                                    handleSelect(document)
                                                                }
                                                            }}
                                                            className="document-link" padding="default" align="left"
                                                        >
                                                            {
                                                                row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && (type.documentCode === DOCUMENT_TYPE_CODES.retentionReceipt))
                                                                })?.serialNumber
                                                            }
                                                        </TableCell>
                                                    </React.Fragment>
                                                }
                                                {
                                                    isReimbursementInvoice() &&
                                                    <RefundsCell
                                                        documentId={row.documentId}
                                                    />
                                                }
                                                {
                                                    isForeignExpensesLiquidationNotFinished() &&
                                                    <React.Fragment>
                                                        <TableCell
                                                            onClick={(e) => {
                                                                const document = row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && (type.documentCode === DOCUMENT_TYPE_CODES.retentionReceipt))
                                                                })
                                                                if (document) {
                                                                    e.stopPropagation()
                                                                    handleSelect(document)
                                                                }
                                                            }}
                                                            className="document-link" padding="default" align="left"
                                                        >
                                                            {
                                                                row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && (type.documentCode === DOCUMENT_TYPE_CODES.retentionReceipt))
                                                                })?.serialNumber
                                                            }
                                                        </TableCell>
                                                        <TableCell
                                                            onClick={(e) => {
                                                                const document = row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && (type.documentCode === DOCUMENT_TYPE_CODES.purchaseSettlement))
                                                                })
                                                                if (document) {
                                                                    e.stopPropagation()
                                                                    handleSelect(document)
                                                                }
                                                            }}
                                                            className="document-link" padding="default" align="left"
                                                        >
                                                            {
                                                                row.associated.find(r => {
                                                                    const type = documentTypes.find(d => d.documentTypeId === r.documentTypeId)
                                                                    return (r.documentTypeId === type?.documentTypeId && (type.documentCode === DOCUMENT_TYPE_CODES.purchaseSettlement))
                                                                })?.serialNumber
                                                            }
                                                        </TableCell>
                                                    </React.Fragment>
                                                }
                                                {
                                                    isPurchaseSettlement() &&
                                                    <TableCell
                                                        onClick={(e) => {
                                                            const document = row.associated.find(r =>
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.foreignExpensesLiquidation ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.employeeReimbursement ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.cashReimbursement
                                                            )
                                                            if (document) {
                                                                e.stopPropagation()
                                                                handleSelect(document)
                                                            }
                                                        }}
                                                        className="document-link" padding="default" align="left"
                                                    >
                                                        {
                                                            row.associated.find(r =>
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.foreignExpensesLiquidation ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.employeeReimbursement ||
                                                                r.documentTypeId === DOCUMENT_TYPE_IDS.cashReimbursement
                                                            )?.serialNumber
                                                        }
                                                    </TableCell>
                                                }
                                            </TableRow>
                                        )
                                    }
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
                <Box>

                    <TablePagination
                        rowsPerPageOptions={MEDIUM_ROWS_PER_PAGINATION_OPTIONS}
                        component="div"
                        count={totalDocuments}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={async (event, pageNumber) => {
                            if (pageNumber > page && ((pageNumber + 1) * rowsPerPage) > documentsTable.length && documentsTable.length < totalDocuments) {
                                await handleNewPage(pageNumber, rowsPerPage)
                            }
                            handleChangePage(event, pageNumber)
                        }}
                        onRowsPerPageChange={async (event) => {
                            if (parseInt(event.target.value, 10) > rowsPerPage && parseInt(event.target.value, 10) > documentsTable.length && documentsTable.length < totalDocuments) {
                                await handleNewPage(page, parseInt(event.target.value, 10))
                            }
                            handleChangeRowsPerPage(event as any)
                        }}
                        labelRowsPerPage="Documentos por página"
                        labelDisplayedRows={LabelDisplayedRows}
                    />
                </Box>
            </div >
            <Snackbar
                open={messageConfig.open}
                autoHideDuration={6000}
                onClose={() => setMessageConfig({ ...messageConfig, open: false })}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                <Alert
                    variant="filled"
                    onClose={() => setMessageConfig({ ...messageConfig, open: false })}
                    severity={messageConfig.severity}
                >
                    {messageConfig.message}
                </Alert>
            </Snackbar>
            <FixedLoadingIndicator loading={loading} />
            <DocumentPicker
                open={openPicker}
                onClose={() => setOpenPicker(false)}
            />
            <ServerErrorHandler
                error={error}
                onSuccess={() => setError(false)}
                tryAgain={getDocumentsByInitialFilters}
            />
        </Paper >
    )
}

export default DocumentsDashboard